/**
 * Home sign-in — the BROWSER half of the OIDC ceremony.
 *
 * Build a `site-login` authorize request to the person's Home with PKCE S256 + state + nonce
 * (`@agenticprimitives/connect-client`), stash the verifier/nonce/state in `sessionStorage`, and
 * navigate. The code that comes back is handed STRAIGHT to the API Worker, which exchanges and
 * verifies it itself. This app never sees an id_token and never decides who the person is.
 */

import { connectViaRedirect, createConnectClient, generatePkce, randomB64url, type ConnectClient, type ConnectStash } from '@agenticprimitives/connect-client';

export interface AuthConfig {
  devAuth: boolean;
  demoPractice: boolean;
  /** The wearable provider this deployment connects, or null when it connects none. */
  devices?: { id: string; label: string; supplies: string } | null;
  home: { clientId: string; origin: string; zone: string; delegate: string; redirectUri: string | null; redeemTemplate?: string };
}

const CALLBACK_PARAMS = ['code', 'state', 'error', 'error_description', 'error_uri', 'iss', 'session_state', 'ac_relay', 'ac_iss', 'treasury', 'treasury_status', 'treasury_error', 'org', 'org_status'];

export const STASH_KEY = 'oncocoach.home.stash';
export const MANDATE_STASH_KEY = 'oncocoach.home.mandate';
export const ORG_STASH_KEY = 'oncocoach.home.org';
export const REDEEM_TEMPLATE = 'oncocoach-redeem';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function sessionStore(): StorageLike | null {
  try {
    const s = globalThis.sessionStorage;
    if (!s) return null;
    const probe = `${STASH_KEY}.probe`;
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

export function writeStash(store: StorageLike | null, stash: ConnectStash, key = STASH_KEY): boolean {
  if (!store) return false;
  try {
    store.setItem(key, JSON.stringify(stash));
    return true;
  } catch {
    return false;
  }
}

export function readStash(store: StorageLike | null, key = STASH_KEY): ConnectStash | null {
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as Partial<ConnectStash>;
    if (typeof s.state === 'string' && typeof s.authOrigin === 'string' && typeof s.codeVerifier === 'string' && typeof s.nonce === 'string' && s.state && s.codeVerifier && s.nonce) {
      return { name: typeof s.name === 'string' ? s.name : '', state: s.state, authOrigin: s.authOrigin, codeVerifier: s.codeVerifier, nonce: s.nonce };
    }
    return null;
  } catch {
    return null;
  }
}

export function clearStash(store: StorageLike | null, key = STASH_KEY): void {
  try {
    store?.removeItem(key);
  } catch {
    /* single-use anyway */
  }
}

/** SEC-018 issuer allowlist, mirroring the Worker's rule. The Worker's copy decides. */
export function isAllowedHomeOrigin(zone: string, origin: string): boolean {
  const z = zone.trim().toLowerCase();
  if (!z) return false;
  let u: URL;
  try {
    u = new URL(origin);
  } catch {
    return false;
  }
  if (u.pathname !== '/' && u.pathname !== '') return false;
  if (u.search !== '' || u.hash !== '') return false;
  const host = u.hostname.toLowerCase();
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  if (isLocal) return z === 'localhost';
  if (u.protocol !== 'https:') return false;
  if (host === z) return true;
  if (!host.endsWith(`.${z}`)) return false;
  return /^[a-z0-9][a-z0-9-]{0,62}$/.test(host.slice(0, -(z.length + 1)));
}

export type Callback = { kind: 'code'; code: string; state: string } | { kind: 'error'; error: string; description?: string };

export function parseCallback(href: string): Callback | null {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return null;
  }
  const q = u.searchParams;
  const error = q.get('error');
  if (error) return { kind: 'error', error, description: q.get('error_description') ?? undefined };
  const code = q.get('code');
  const state = q.get('state');
  if (code && state) return { kind: 'code', code, state };
  return null;
}

export function stripAuthParams(href: string): string {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return href;
  }
  for (const p of CALLBACK_PARAMS) u.searchParams.delete(p);
  const query = u.searchParams.toString();
  return `${u.origin}${u.pathname}${query ? `?${query}` : ''}${u.hash}`;
}

export function describeCallbackError(error: string, description?: string): string {
  if (error === 'access_denied') return 'Sign-in was cancelled at your Home.';
  return description ? `Your Home refused the sign-in: ${description} (${error})` : `Your Home refused the sign-in (${error}).`;
}

export function homeClient(config: AuthConfig): ConnectClient {
  const redirectUri = config.home.redirectUri ?? `${location.origin}/`;
  return createConnectClient({
    clientId: config.home.clientId,
    delegate: config.home.delegate as `0x${string}`,
    redirectUri: () => redirectUri,
    resolveAuthOrigin: () => config.home.origin,
    isAllowedIssuerOrigin: (o) => isAllowedHomeOrigin(config.home.zone, o),
    scope: 'openid agent',
  });
}

export const PROFILE_NAME_MAX = 40;
export const PROFILE_NAME_KEY = 'oncocoach.profileName';

/** A display name, cleaned but not reshaped: one line, no control or zero-width characters. */
export function toProfileName(input: string): string {
  const cleaned = Array.from(input.replace(/\s+/g, ' '))
    .filter((ch) => {
      const c = ch.codePointAt(0) ?? 0;
      const control = c < 32 || c === 127;
      const zeroWidth = (c >= 0x200b && c <= 0x200f) || c === 0x2028 || c === 0x2029 || c === 0xfeff;
      return !control && !zeroWidth;
    })
    .join('');
  return cleaned.replace(/^ +/, '').slice(0, PROFILE_NAME_MAX);
}


export function finishProfileName(input: string): string {
  return toProfileName(input).trim();
}

export function rememberProfileName(store: StorageLike | null, name: string): void {
  const clean = finishProfileName(name);
  try {
    if (clean) store?.setItem(PROFILE_NAME_KEY, clean);
    else store?.removeItem(PROFILE_NAME_KEY);
  } catch {
    /* a lost display name is not a lost sign-in */
  }
}

export function takeProfileName(store: StorageLike | null = sessionStore()): string {
  let raw: string | null = null;
  try {
    raw = store?.getItem(PROFILE_NAME_KEY) ?? null;
    store?.removeItem(PROFILE_NAME_KEY);
  } catch {
    return '';
  }
  return finishProfileName(raw ?? '');
}

export async function startHomeSignIn(config: AuthConfig, name = '', store: StorageLike | null = sessionStore()): Promise<string> {
  if (!config.home.clientId || !config.home.origin) throw new Error('This deployment has no Home configured.');
  if (!isAllowedHomeOrigin(config.home.zone, config.home.origin)) throw new Error(`Refusing to sign in at ${config.home.origin}: it is not a trusted Home for this site.`);
  const { url, stash } = await connectViaRedirect(homeClient(config));
  if (!writeStash(store, stash)) throw new Error('This browser will not let the site keep a sign-in secret (session storage is blocked), so sign-in cannot complete.');
  rememberProfileName(store, name);
  return url;
}

export type CallbackOutcome = { status: 'none' } | { status: 'signed-in'; code: string; codeVerifier: string; authOrigin: string; nonce: string; state: string } | { status: 'error'; message: string };

export function consumeCallback(href: string, store: StorageLike | null, key = STASH_KEY): CallbackOutcome {
  const cb = parseCallback(href);
  if (!cb) return { status: 'none' };
  const stash = readStash(store, key);
  if (cb.kind === 'error') return { status: 'error', message: describeCallbackError(cb.error, cb.description) };
  if (!stash) return { status: 'error', message: 'This sign-in could not be matched to a request from this browser. Start again.' };
  if (stash.state !== cb.state) return { status: 'error', message: 'The sign-in did not match the request this browser started (state mismatch). Start again.' };
  return { status: 'signed-in', code: cb.code, codeVerifier: stash.codeVerifier, authOrigin: stash.authOrigin, nonce: stash.nonce, state: cb.state };
}

let callbackConsumed = false;

function scrubUrl(): void {
  try {
    history.replaceState(null, '', stripAuthParams(location.href));
  } catch {
    /* not a reason to fail */
  }
}

export function takeHomeCallback(store: StorageLike | null = sessionStore()): CallbackOutcome {
  if (callbackConsumed) return { status: 'none' };
  const outcome = consumeCallback(location.href, store);
  if (outcome.status === 'none') return outcome;
  callbackConsumed = true;
  clearStash(store);
  scrubUrl();
  return outcome;
}

/** A ceremony that is NOT a sign-in (mandate, org-create) is told apart by which stash's state matches. */
function takeCeremonyCallback(key: string, store: StorageLike | null): CallbackOutcome {
  if (callbackConsumed) return { status: 'none' };
  const cb = parseCallback(location.href);
  if (!cb || cb.kind === 'error') return { status: 'none' };
  const stash = readStash(store, key);
  if (!stash || stash.state !== cb.state) return { status: 'none' };
  const outcome = consumeCallback(location.href, store, key);
  if (outcome.status === 'none') return outcome;
  callbackConsumed = true;
  clearStash(store, key);
  scrubUrl();
  return outcome;
}

async function startTemplateCeremony(config: AuthConfig, template: string, key: string, store: StorageLike | null, extra: Record<string, string> = {}): Promise<string> {
  if (!config.home.clientId || !config.home.origin) throw new Error('This deployment has no Home configured.');
  if (!isAllowedHomeOrigin(config.home.zone, config.home.origin)) throw new Error(`Refusing to send you to ${config.home.origin}: it is not a trusted Home for this site.`);
  const client = homeClient(config);
  const pkce = await generatePkce();
  const stash: ConnectStash = { name: '', state: randomB64url(16), authOrigin: config.home.origin, codeVerifier: pkce.verifier, nonce: randomB64url(16) };
  if (!writeStash(store, stash, key)) throw new Error('This browser will not let the site keep a secret (session storage is blocked), so the ceremony cannot complete.');
  const url = new URL(client.buildAuthorizeUrl({ authOrigin: stash.authOrigin, state: stash.state, nonce: stash.nonce, codeChallenge: pkce.challenge, agentName: '', template: template as never }));
  for (const [k, v] of Object.entries(extra)) url.searchParams.set(k, v);
  return url.toString();
}

/** Ask the person's Home to authorise redemptions from their treasury (`oncocoach-redeem`). */
export function startRedeemMandate(config: AuthConfig, maxAmountBaseUnits: string | null, store: StorageLike | null = sessionStore()): Promise<string> {
  return startTemplateCeremony(config, config.home.redeemTemplate ?? REDEEM_TEMPLATE, MANDATE_STASH_KEY, store, maxAmountBaseUnits && /^\d+$/.test(maxAmountBaseUnits) ? { pay_amount: maxAmountBaseUnits } : {});
}

export function takeMandateCallback(store: StorageLike | null = sessionStore()): CallbackOutcome {
  return takeCeremonyCallback(MANDATE_STASH_KEY, store);
}

/** Ask the person's Home to found the practice's `.org` Smart Agent (`org-create`). */
export function startOrgCreation(config: AuthConfig, label: string, purpose: string, store: StorageLike | null = sessionStore()): Promise<string> {
  return startTemplateCeremony(config, 'org-create', ORG_STASH_KEY, store, { org_base: label, org_purpose: purpose });
}

export function takeOrgCallback(store: StorageLike | null = sessionStore()): CallbackOutcome {
  return takeCeremonyCallback(ORG_STASH_KEY, store);
}
